import React from "react";
import InfoCard from "./InfoCard/InfoCard";
import { LocationData } from "../../types/";
import counterGif from "../../styles/images/gif/counter.gif";
import leafGif from "../../styles/images/gif/leaf.gif";
import panelGif from "../../styles/images/gif/panel.gif";
import thunderGif from "../../styles/images/gif/thunder.gif";

interface InformationPointProps {
  locationData?: LocationData;
}

const InformationPoint: React.FC<InformationPointProps> = ({ locationData }) => {
  const isLoading = !locationData || !locationData.items || locationData.items.length === 0;

  // Check if data is in fallback/no-data state
  const isFallbackData = locationData?.items?.[0]?.value === "---";

  const defaultData = {
    title: "",
    value: "",
    unit: "",
    description: "",
    timestamp: "",
  };

  // Format unit display to ensure proper spacing
  const formatUnitDisplay = (text: string, unit: string): string => {
    if (!text) return "";
    // If it already has space before the unit, keep as is
    if (text.includes(` ${unit}`)) return text;
    // If unit appears without space, add space
    const regex = new RegExp(`(\\d+[.,]?\\d*)${unit}`, "g");
    if (text.includes(unit) && regex.test(text)) {
      return text.replace(regex, `$1 ${unit}`);
    }
    // If it doesn't have the unit at all, add with space
    if (!text.includes(unit)) {
      return `${text} ${unit}`;
    }
    return text;
  };

  // Helper function to  get description for fallback data
  const getDescription = (index: number): string => {
    if (isLoading) return defaultData.description;
    if (isFallbackData) return "";

    // Normal data processing
    switch (index) {
      case 1:
        return formatUnitDisplay(locationData.items[1].footer?.all || "", "MWh");
      case 2:
        return formatUnitDisplay(locationData.items[2].footer?.all || "", "t");
      case 3:
        const label = locationData.items[3].descp?.parts?.[1]?.label || "";
        const value = locationData.items[3].descp?.parts?.[1]?.value || "";
        return label + " " + value;
      default:
        return "";
    }
  };

  // Helper function to get timestamp
  const getTimestamp = (index: number): string => {
    if (isLoading) return defaultData.timestamp;
    if (isFallbackData) {
      return locationData.items[index].timestamp || "";
    }

    // Normal data processing
    switch (index) {
      case 0:
        return "Há " + (locationData.items[0].timestamp || "");
      case 1:
      case 2:
        return locationData.items[index].descp?.descp || "";
      case 3:
        return "Potência Instalada";
      default:
        return "";
    }
  };

  return (
    <div className="Information">
      <div className="Title">Energias Renováveis</div>
      <div className="Location">Produção de Energia Renovável {locationData?.title}</div>
      <div className="Separator"></div>
      <div className="CardContainer">
        <InfoCard
          key="0"
          loading={isLoading}
          title={"Potência FV actual"}
          value={isLoading ? defaultData.value : locationData.items[0].value}
          unit="kW"
          description=""
          gif={counterGif}
          timestamp={getTimestamp(0)}
        />
        <InfoCard
          key="1"
          loading={isLoading}
          title={"Energia fotovoltaica"}
          value={
            isLoading
              ? defaultData.value
              : isFallbackData
              ? locationData.items[1].value
              : locationData.items[1].value.includes(",")
              ? locationData.items[1].value
              : (Number(locationData.items[1].value) / 1000).toFixed(2)
          }
          unit={"kWh"}
          description={getDescription(1)}
          timestamp={getTimestamp(1)}
          gif={thunderGif}
        />
        <InfoCard
          key="2"
          loading={isLoading}
          title={"Redução das emissões de CO2"}
          value={isLoading ? defaultData.value : locationData.items[2].value}
          unit={"kg"}
          description={getDescription(2)}
          timestamp={getTimestamp(2)}
          gif={leafGif}
        />
        <InfoCard
          key="3"
          loading={isLoading}
          title={"Informações sobre o sistema"}
          value={
            isLoading ? defaultData.value : isFallbackData ? locationData.items[3].value : locationData.items[3].descp?.parts[0].value?.match(/[\d,]+/)?.[0]
          }
          unit="kWp"
          timestamp={getTimestamp(3)}
          description={getDescription(3)}
          gif={panelGif}
        />
      </div>
    </div>
  );
};

export default InformationPoint;
